import { useCallback, useEffect, useState } from 'react';

import axios, { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import {
    type FormActionType,
    type LoadingStatus,
    type NewApiKey,
    type UpdateApiKey
} from './components/AdminApiKeys/types';
import {
    API_KEYS_CREATE,
    API_KEYS_DELETE,
    API_KEYS_GET,
    API_KEYS_REGENERATE,
    API_KEYS_UPDATE
} from '../../config/apiPaths';
import { type ApiKey, type CreatedApiKeyResponse } from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';

type ApiKeyErrorResponse = {
    error: string;
};

const useApiKeys = () => {
    const { uuid, isAdmin } = useContainer(AdminAuthContainer);
    const { t } = useTranslation('admin_settings');
    const { enqueueSnackbar } = useSnackbar();
    const { api } = getUrls();

    const [apiKeyFetchStatus, setApiKeyFetchStatus] = useState<LoadingStatus>('INITIAL');
    const [apiKeyFetchError, setApiKeyFetchError] = useState<AxiosError<ApiKeyErrorResponse> | undefined>(undefined);
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);

    const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
    const [keyValue, setKeyValue] = useState<string>('');
    const [formActionType, setFormActionType] = useState<FormActionType>('CREATE');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

    const fetchKeys = useCallback(
        async (userUuid: string) => {
            try {
                setApiKeyFetchStatus('LOADING');
                const { data: keys } = await axios.get<ApiKey[]>(`${api}${API_KEYS_GET}`, {
                    headers: getUserUuidHeaders(userUuid)
                });

                setApiKeys(keys);
                setApiKeyFetchStatus('SUCCESS');
            } catch (error) {
                console.error(error);
                if (error instanceof AxiosError) {
                    setApiKeyFetchError(error);
                }

                setApiKeyFetchStatus('ERROR');
            }
        },
        [api]
    );

    const createApiKey = async (apiKey: NewApiKey) => {
        try {
            setIsSaving(true);
            const {
                data: { rawValue, key: createdKey }
            } = await axios.post<CreatedApiKeyResponse>(`${api}${API_KEYS_CREATE}`, { user_uuid: uuid, ...apiKey });
            setIsSaving(false);

            const successMessage = t('api-keys.actions.create-success', { keyName: createdKey.name });
            enqueueSnackbar(successMessage, { variant: 'success' });

            setSelectedKey(createdKey);
            setKeyValue(rawValue);
            setApiKeys((prevState) => [...prevState, createdKey]);
        } catch (error) {
            console.error(error);
            setIsSaving(false);
            const errorMessage = t('api-keys.actions.create-error', { keyName: apiKey.name });
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const updateApiKey = async (apiKey: UpdateApiKey) => {
        try {
            setIsSaving(true);
            const { data: updatedKey } = await axios.patch<ApiKey>(`${api}${API_KEYS_UPDATE}`, {
                user_uuid: uuid,
                ...apiKey
            });
            setIsSaving(false);

            const successMessage = t('api-keys.actions.update-success', { keyName: updatedKey.name });
            enqueueSnackbar(successMessage, { variant: 'success' });

            setApiKeys((prevState) =>
                prevState.map((key) => {
                    if (key.id === updatedKey.id) {
                        return updatedKey;
                    }

                    return key;
                })
            );
        } catch (error) {
            setIsSaving(false);
            const errorMessage = t('api-keys.actions.update-error', { keyName: apiKey.name });
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const deleteApiKey = async (apiKey: ApiKey) => {
        try {
            setIsDeleting(true);
            const {
                data: [deletedKey]
            } = await axios.delete<ApiKey[]>(`${api}${API_KEYS_DELETE}?ids=${apiKey.id}`, {
                headers: getUserUuidHeaders(uuid)
            });
            setIsDeleting(false);

            const successMessage = t('api-keys.actions.delete-success', { keyName: deletedKey.name });
            enqueueSnackbar(successMessage, { variant: 'success' });

            setApiKeys((prevState) => prevState.filter((key) => key.id !== deletedKey.id));
        } catch (error) {
            setIsDeleting(false);
            console.error(error);
            const errorMessage = t('api-keys.actions.delete-error', { keyName: apiKey.name });
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const regenerateApiKey = async (apiKey: ApiKey | null) => {
        try {
            setIsRegenerating(true);
            const {
                data: { rawValue, key }
            } = await axios.post(`${api}${API_KEYS_REGENERATE}`, {
                user_uuid: uuid,
                id: apiKey?.id
            });
            setSelectedKey(key);
            setKeyValue(rawValue);
            setIsRegenerating(false);
        } catch (error) {
            setIsRegenerating(false);
            console.log(error);
            const errorMessage = t('api-keys.actions.regenerate-error', { keyName: apiKey?.name });
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    useEffect(() => {
        if (apiKeyFetchStatus === 'INITIAL' && uuid && isAdmin) {
            fetchKeys(uuid);
        }
    }, [api, uuid, apiKeyFetchStatus, fetchKeys, isAdmin]);

    return {
        apiKeys,
        apiKeyFetchStatus,
        apiKeyFetchError,

        createApiKey,
        updateApiKey,
        deleteApiKey,
        regenerateApiKey,

        selectedKey,
        setSelectedKey,
        keyValue,
        setKeyValue,

        formActionType,
        setFormActionType,

        isSaving,
        isDeleting,
        isRegenerating
    };
};

const ApiKeysContainer = createContainer(useApiKeys);

export default ApiKeysContainer;
